// Phygital · Admin Control Bar
// Floating widget in bottom-right, ONLY renders if the signed-in user is in the admin cohort.

import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Beaker, EyeOff, RefreshCw, Settings, Sparkles, Trash2, Users, X, Zap,
} from "lucide-react";
import {
  usePhygitalFlags, getOverrides, setOverride, clearOverrides,
  getViewAsCohort, setViewAsCohort, refreshFlags,
} from "./hooks/usePhygitalFlags";
import { trackEvent } from "./hooks/useTracker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TEST_ACCOUNTS = [
  { key: "aria",    name: "Aria (demo, empty-friendly)", token: "ml_aria_phygital_demo_2026",    user_id: "usr_aria_demo" },
  { key: "shelsea", name: "Shelsea Patton (host)",       token: "ml_shelsea_phygital_pilot_2026", user_id: "usr_shelsea_patton" },
  { key: "mia",     name: "Mia Thomas (host)",           token: "ml_mia_phygital_pilot_2026",     user_id: "usr_mia_makeup" },
  { key: "admin",   name: "Admin (super-admin)",         token: "ml_naomi_admin_phygital2026",    user_id: "user_5ad2f737c8b1" },
  { key: "amayah",  name: "Amayah (pilot)",              token: "ml_amayah_phygital_pilot_2026",  user_id: "usr_amayah" },
  { key: "micah",   name: "Micah (blank pilot)",         token: "ml_micah_phygital_pilot_2026",   user_id: "usr_micah" },
];

const COHORT_OPTIONS = [
  { id: "",      label: "View as: real cohorts (default)" },
  { id: "all",   label: "View as: all (public)" },
  { id: "pilot", label: "View as: pilot" },
  { id: "alpha", label: "View as: alpha" },
  { id: "beta",  label: "View as: beta" },
];

function authHeader() {
  const t = localStorage.getItem("session_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}

async function signInAs(token, key) {
  try {
    trackEvent("admin.signin_as", { account: key }, { surface: "admin_bar" });
    const r = await axios.post(`${API}/auth/verify-magic-link`, { token });
    if (r.data.session_token) {
      localStorage.setItem("session_token", r.data.session_token);
      refreshFlags();
      window.location.reload();
    }
  } catch {
    toast.error("Sign-in failed");
  }
}

async function adminAction(path, currentUserId) {
  try {
    trackEvent("admin.action", { path, user_id: currentUserId }, { surface: "admin_bar" });
    await axios.post(`${API}/phygital/admin/${path}/${currentUserId}`, {}, { headers: authHeader() });
    return true;
  } catch {
    return false;
  }
}

export default function AdminBar() {
  const [me, setMe] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const { flags, cohorts, loading } = usePhygitalFlags();
  const overrides = getOverrides();
  const viewAs = getViewAsCohort();

  useEffect(() => {
    const t = localStorage.getItem("session_token");
    if (!t) { setMe(null); return; }
    axios.get(`${API}/auth/me`, { headers: authHeader() })
      .then((r) => setMe(r.data))
      .catch(() => setMe(null));
  }, []);

  const isAdmin = !!(me && (me.is_admin || me.is_super_admin));
  if (loading || !me || !isAdmin || !flags.admin_control_bar) return null;

  const flagIds = Object.keys(flags).sort();

  const runAction = async (path, label) => {
    if (!me?.user_id) return;
    setBusy(true);
    const ok = await adminAction(path, me.user_id);
    setBusy(false);
    if (ok) {
      toast.success(`${label} · reload to see`);
      setTimeout(() => window.location.reload(), 400);
    } else toast.error(`${label} failed`);
  };

  return (
    <div className="fixed bottom-4 right-4 z-[9999]" data-testid="admin-control-bar">
      {!open ? (
        <button
          data-testid="admin-control-bar-open"
          onClick={() => setOpen(true)}
          className="w-12 h-12 rounded-full bg-stone-900 text-white shadow-lg flex items-center justify-center hover:scale-105 transition"
          title="Admin control bar"
        >
          <Zap className="w-5 h-5" />
        </button>
      ) : (
        <div className="w-80 max-h-[80vh] overflow-y-auto rounded-2xl bg-white border-2 border-stone-900 shadow-2xl">
          <div className="sticky top-0 bg-stone-900 text-white px-4 py-2 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4" />
              <p className="text-xs font-bold tracking-wider uppercase">Admin control</p>
            </div>
            <button onClick={() => setOpen(false)} className="text-white/70 hover:text-white" data-testid="admin-control-bar-close">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="px-4 py-3 border-b border-stone-100">
            <p className="text-[9px] uppercase tracking-wider text-stone-500 font-bold">Signed in as</p>
            <p className="text-sm font-bold text-stone-900 truncate" data-testid="admin-current-user">{me.name || me.email}</p>
            <p className="text-[10px] text-stone-500 truncate">{me.user_id}</p>
            <div className="mt-1 flex flex-wrap gap-1">
              {cohorts.map((c) => (
                <span key={c} className="text-[9px] font-bold uppercase tracking-wider bg-stone-100 text-stone-700 rounded-full px-2 py-0.5">{c}</span>
              ))}
            </div>
          </div>

          <details className="border-b border-stone-100" open>
            <summary className="px-4 py-2 text-[10px] uppercase tracking-wider font-bold text-stone-700 cursor-pointer inline-flex items-center gap-1.5">
              <Users className="w-3 h-3" /> Sign in as test user
            </summary>
            <div className="px-4 pb-3 space-y-1">
              {TEST_ACCOUNTS.map((a) => (
                <button key={a.key} data-testid={`admin-signin-${a.key}`}
                  onClick={() => signInAs(a.token, a.key)}
                  className={`w-full text-left rounded-lg border px-2 py-1.5 text-xs hover:border-stone-900 transition
                    ${me.user_id === a.user_id ? "border-stone-900 bg-stone-50 font-bold" : "border-stone-200 text-stone-700"}`}>
                  {a.name}
                </button>
              ))}
            </div>
          </details>

          <details className="border-b border-stone-100" open>
            <summary className="px-4 py-2 text-[10px] uppercase tracking-wider font-bold text-stone-700 cursor-pointer inline-flex items-center gap-1.5">
              <Beaker className="w-3 h-3" /> State (current user)
            </summary>
            <div className="px-4 pb-3 grid grid-cols-2 gap-1.5">
              <button disabled={busy} data-testid="admin-seed-empty" onClick={() => runAction("seed-user/" + me.user_id + "?state=empty", "Empty state")}
                className="text-[11px] rounded-lg border border-stone-300 py-1.5 hover:border-stone-900 disabled:opacity-50 col-span-2">
                Reset to empty
              </button>
              <button disabled={busy} data-testid="admin-seed-half" onClick={() => runAction("seed-user/" + me.user_id + "?state=half", "Half-filled state")}
                className="text-[11px] rounded-lg border border-stone-300 py-1.5 hover:border-stone-900 disabled:opacity-50">
                Seed half
              </button>
              <button disabled={busy} data-testid="admin-seed-full" onClick={() => runAction("seed-user/" + me.user_id + "?state=full", "Full state")}
                className="text-[11px] rounded-lg border border-stone-300 py-1.5 hover:border-stone-900 disabled:opacity-50">
                Seed full
              </button>
              <button disabled={busy} data-testid="admin-hard-reset" onClick={() => runAction("reset-user", "Hard reset")}
                className="text-[11px] rounded-lg border border-rose-300 text-rose-700 py-1.5 hover:border-rose-600 disabled:opacity-50 col-span-2 inline-flex items-center justify-center gap-1">
                <Trash2 className="w-3 h-3" /> Hard reset (delete all)
              </button>
            </div>
          </details>

          <details className="border-b border-stone-100">
            <summary className="px-4 py-2 text-[10px] uppercase tracking-wider font-bold text-stone-700 cursor-pointer inline-flex items-center gap-1.5">
              <EyeOff className="w-3 h-3" /> View as cohort
            </summary>
            <div className="px-4 pb-3">
              <select
                data-testid="admin-view-as-cohort"
                value={viewAs || ""}
                onChange={(e) => { setViewAsCohort(e.target.value); refreshFlags(); window.location.reload(); }}
                className="w-full rounded-lg border border-stone-300 text-xs px-2 py-1.5">
                {COHORT_OPTIONS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
              </select>
              <p className="text-[9px] text-stone-500 mt-1">Preview requires reload; overrides are cleared.</p>
            </div>
          </details>

          <details className="border-b border-stone-100" open>
            <summary className="px-4 py-2 text-[10px] uppercase tracking-wider font-bold text-stone-700 cursor-pointer inline-flex items-center gap-1.5">
              <Settings className="w-3 h-3" /> Flag overrides ({Object.keys(overrides).length})
            </summary>
            <div className="px-4 pb-3 space-y-1">
              {flagIds.map((id) => {
                const cur = !!flags[id];
                const over = overrides[id];
                const isOverridden = over !== undefined;
                return (
                  <div key={id} className="flex items-center gap-2 text-[11px]" data-testid={`admin-flag-row-${id}`}>
                    <span className={`flex-1 truncate ${isOverridden ? "font-bold" : ""}`}>{id}</span>
                    <button onClick={() => { setOverride(id, !cur); trackEvent("admin.flag_toggle", { flag_id: id, new_value: !cur }, { surface: "admin_bar" }); }} data-testid={`admin-flag-toggle-${id}`}
                      className={`w-8 h-4 rounded-full transition ${cur ? "bg-emerald-500" : "bg-stone-300"} relative`}>
                      <span className={`absolute top-0.5 w-3 h-3 rounded-full bg-white transition ${cur ? "left-4" : "left-0.5"}`} />
                    </button>
                    {isOverridden && (
                      <button onClick={() => setOverride(id, null)} data-testid={`admin-flag-clear-${id}`}
                        className="text-stone-400 hover:text-rose-600" title="Clear override">
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                );
              })}
              <button data-testid="admin-clear-overrides"
                onClick={() => { clearOverrides(); toast.success("Overrides cleared"); }}
                className="w-full text-[10px] uppercase tracking-wider font-bold text-stone-700 hover:text-stone-900 mt-2 py-1 border-t border-stone-100 pt-2">
                Clear all overrides
              </button>
            </div>
          </details>

          <details className="border-b border-stone-100">
            <summary className="px-4 py-2 text-[10px] uppercase tracking-wider font-bold text-stone-700 cursor-pointer inline-flex items-center gap-1.5">
              <Sparkles className="w-3 h-3" /> Quick links
            </summary>
            <div className="px-4 pb-3 space-y-1 text-xs">
              <a href="/me" data-testid="admin-link-plan" onClick={() => trackEvent("admin.link_click", {href:"/me"}, {surface:"admin_bar"})} className="block text-purple-700 hover:underline">→ /me (personal dashboard - opens Plan a Service popup)</a>
              <a href="/design/components" data-testid="admin-link-components" onClick={() => trackEvent("admin.link_click", {href:"/design/components"}, {surface:"admin_bar"})} className="block text-purple-700 hover:underline">→ /design/components (component library)</a>
              <a href="/me" data-testid="admin-link-me" onClick={() => trackEvent("admin.link_click", {href:"/me"}, {surface:"admin_bar"})} className="block text-purple-700 hover:underline">→ /me (my dashboard)</a>
              <a href={`${API}/phygital/admin/events?limit=50`} data-testid="admin-link-events" target="_blank" rel="noopener noreferrer" className="block text-purple-700 hover:underline">→ recent tracked events (JSON)</a>
              <a href={`${API}/phygital/catalog`} target="_blank" rel="noopener noreferrer" className="block text-purple-700 hover:underline">→ /api/phygital/catalog (raw)</a>
              <a href={`${API}/phygital/flags`} target="_blank" rel="noopener noreferrer" className="block text-purple-700 hover:underline">→ /api/phygital/flags (raw, needs auth)</a>
            </div>
          </details>

          <div className="px-4 py-2 flex items-center justify-between text-[9px] text-stone-500">
            <span className="uppercase tracking-wider">Phygital · v1</span>
            <button onClick={() => { refreshFlags(); window.location.reload(); }} className="inline-flex items-center gap-1 hover:text-stone-900" data-testid="admin-refresh">
              <RefreshCw className="w-3 h-3" /> refresh
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
